import { useEffect, useId, useRef, type ReactNode } from 'react'
import { Button } from './Button'

type Props = {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  busyLabel?: string
  danger?: boolean
  busy?: boolean
  requireReason?: boolean
  reasonMinLength?: number
  onCancel: () => void
  onConfirm: (reason: string) => void
}

/**
 * Premium in-app confirm dialog. Never use window.alert / confirm / prompt.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busyLabel = 'Working…',
  danger,
  busy,
  requireReason,
  reasonMinLength = 3,
  onCancel,
  onConfirm,
}: Props) {
  const titleId = useId()
  const reasonRef = useRef<HTMLTextAreaElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    if (requireReason) reasonRef.current?.focus()
    else panelRef.current?.querySelector<HTMLElement>('button, [href], input, textarea')?.focus()

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault()
        onCancel()
      }
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, requireReason, busy, onCancel])

  if (!open) return null

  return (
    <div
      className="ap-dialog-backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onCancel()
      }}
    >
      <div
        ref={panelRef}
        className="ap-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId}>{title}</h2>
        <div className="ap-dialog__body">{body}</div>
        {requireReason ? (
          <label className="cp-field">
            Reason (required)
            <textarea
              ref={reasonRef}
              rows={3}
              minLength={reasonMinLength}
              placeholder="Enter reason…"
              disabled={busy}
            />
          </label>
        ) : null}
        <div className="ap-dialog__actions">
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'secondary' : 'primary'}
            disabled={busy}
            onClick={() => {
              const reason = reasonRef.current?.value?.trim() || ''
              if (requireReason && reason.length < reasonMinLength) return
              onConfirm(reason)
            }}
          >
            {busy ? busyLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
